"use client";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";

const OurEsteemedMentors = ({ courseSections }) => {
  // Extract EsteemedMentors from course data
  const mentors = courseSections?.EsteemedMentors || [];

  // Function to check if URL is a video file
  const isVideoFile = (url) => {
    return url?.match(/\.(mp4|webm|ogg|mov)$/i);
  };

  // Don't render if no mentors
  if (!mentors || mentors.length === 0) {
    return null;
  }

  return (
    <section className="relative bg-white page-container my-[50px] md:my-[80px] xl:my-[100px]">
      <div className="flex flex-col lg:flex-row justify-between lg:items-start mb-[30px] md:mb-[40px]">
        <div className="flex-1">
          <h2 className="mb-[12px]">Our Esteemed Mentors</h2>
          <p className="text-black w-full md:w-[80%] xl:w-[70%]">
            A dedicated team of experienced educators and industry specialists
            committed to nurturing students with expert guidance, practical
            insight, and strong academic and professional mentorship.
          </p>
        </div>
        <div className="hidden lg:flex gap-4 mt-4 lg:mt-0">
          <button
            id="mentorPrevButton"
            className="cursor-pointer bg-white border border-gray-300 rounded-full p-3 shadow hover:bg-gray-100 transition"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <button
            id="mentorNextButton"
            className="cursor-pointer bg-white border border-gray-300 rounded-full p-3 shadow hover:bg-gray-100 transition"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      <Swiper
        modules={[Navigation, Autoplay]}
        spaceBetween={20}
        loop={mentors.length > 1}
        slidesPerView={1.2}
        navigation={{
          prevEl: "#mentorPrevButton",
          nextEl: "#mentorNextButton",
        }}
        breakpoints={{
          640: { slidesPerView: 1.5, spaceBetween: 16 },
          768: { slidesPerView: 2.5, spaceBetween: 20 },
          1024: { slidesPerView: 3.5, spaceBetween: 24 },
          1280: { slidesPerView: 4, spaceBetween: 24 },
        }}
        className="mentor-swiper w-full"
      >
        {mentors.map((mentor, index) => {
          const mediaUrl = mentor.MentorMedia?.url;
          const isVideo = isVideoFile(mediaUrl);

          return (
            <SwiperSlide key={mentor.id || index}>
              <div className="bg-white overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                {/* Mentor Image or Video */}
                <div className="relative w-full aspect-[330/341] overflow-hidden">
                 {isVideo ? (
                      <div
                        className="w-full h-full bg-black flex items-center justify-center cursor-pointer"
                        onClick={() => setPlayVideo(true)}
                      >
                        {/* Thumbnail instead of loading video */}
                        <Image
                          src={thumbnail || "/video-placeholder.jpg"}
                          alt="Video thumbnail"
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, 50vw"
                        />

                        {/* Play Button */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          ▶
                        </div>
                      </div>
                    ) : (
                      <Image
                        src={mediaUrl || "/placeholder-mentor.jpg"}
                        alt={mentor?.Name || "Mentor"}
                        fill
                        className="object-cover"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                        priority={index === 0}   // ✅ only first image
                        quality={75}             // ✅ reduce size
                      />
                    )}
                </div>

                {/* Mentor Info */}
                <div className="p-[18px] border-1 border-black border-t-0">
                  <h4 
                    className="text-[20px] font-bold mb-[8px] text-black"
                    style={{ fontFamily: "saans-bold" }}
                  >
                    {mentor.Name}
                  </h4>
                  <p 
                    className="text-[14px] leading-[21px] text-black"
                    style={{ fontFamily: "saans-regular" }}
                  >
                    {mentor.Description}
                  </p>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* Mobile Navigation Buttons */}
      <div className="hidden lg:flex justify-center gap-4 mt-6">
        <button
          id="mentorPrevButton"
          className="cursor-pointer bg-white border border-gray-300 rounded-full p-3 shadow hover:bg-gray-100 transition"
          aria-label="Previous"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
        <button
          id="mentorNextButton"
          className="cursor-pointer bg-white border border-gray-300 rounded-full p-3 shadow hover:bg-gray-100 transition"
          aria-label="Next"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      </div>
    </section>
  );
};

export default OurEsteemedMentors;